import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, throwError, timeout, catchError, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { B2bB2cService } from './b2b-b2c.service';
import { BrowserService } from './browser.service';
import { TranslationService } from './translation.service';

/** Optional settings for `HttpService.post` (third argument may still be a legacy timeout number). */
export type HttpPostOptions = {
  timeout?: number;
  /** When false, do not send `sellingMode` (backend shows all storefront modes). Default true. */
  includeSellingMode?: boolean;
  /** When false, do not send `lang`. Default true. */
  includeLang?: boolean;
  /** Use delivery agent JWT from localStorage */
  useDeliveryToken?: boolean;
};

export type HttpResponseMeta<T> = {
  data: T;
  totalCount: number | null;
};

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private baseUrl = environment.apiUrl;
  private isBrowser: boolean;

  constructor(
    private http: HttpClient,
    private translationService: TranslationService,
    private browserService: BrowserService,
    private b2bB2cService: B2bB2cService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.baseUrl = this.resolveApiBaseUrl();
  }

  /**
   * Production builds served on localhost (e.g. port 4000) must still call the local mb-backend.
   * Set SSR_API_URL when running `node dist/.../server.mjs` for server-side renders.
   */
  private resolveApiBaseUrl(): string {
    const localApi = 'http://localhost:8000/api';

    if (isPlatformServer(this.platformId)) {
      const fromEnv =
        typeof process !== 'undefined' && process.env?.['SSR_API_URL']
          ? String(process.env['SSR_API_URL']).replace(/\/$/, '')
          : '';
      if (fromEnv) {
        return fromEnv;
      }
      if (!environment.production) {
        return localApi;
      }
      const serverApi = (environment as { apiUrlServer?: string }).apiUrlServer;
      return serverApi || environment.apiUrl;
    }

    if (this.isBrowser && typeof location !== 'undefined') {
      const host = location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        // Same-origin `/api` uses Angular's proxy (avoids CORS "Failed to fetch" in the browser).
        // Direct :8000 is still used for SSR / non-dev ports when needed.
        const port = String(location.port || '');
        if (port === '4200' || port === '4000' || port === '') {
          return '/api';
        }
        return localApi;
      }
    }

    return environment.apiUrl;
  }

  private isPublicDeliveryAuthEndpoint(cleanEndpoint: string): boolean {
    if (cleanEndpoint.startsWith('delivery-profile/auth/')) return true;
    if (/^delivery-profile\/agents\/[^/]+\/(contact|public)$/.test(cleanEndpoint)) return true;
    if (cleanEndpoint === 'delivery-profile/complaints/public') return true;
    return false;
  }

  private resolveAuthToken(
    cleanEndpoint: string,
    options?: { useDeliveryToken?: boolean },
  ): string | null {
    if (!this.isBrowser) return null;
    if (this.isPublicDeliveryAuthEndpoint(cleanEndpoint)) {
      return null;
    }
    if (options?.useDeliveryToken || cleanEndpoint.startsWith('delivery-profile/')) {
      return this.browserService.getLocalStorage('delivery_token');
    }
    if (cleanEndpoint.startsWith('courier-platform/') || cleanEndpoint.startsWith('courier/')) {
      return (
        this.browserService.getLocalStorage('courier_token') ||
        this.browserService.getLocalStorage('token')
      );
    }
    return this.browserService.getLocalStorage('token');
  }

  // ✅ FIXED GET method (handles existing ? correctly)
  get<T>(
    endpoint: string,
    params?: any,
    options?: { includeSellingMode?: boolean; includeLang?: boolean; noCache?: boolean; useDeliveryToken?: boolean },
  ): Observable<T> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    this.baseUrl = this.resolveApiBaseUrl();
    let url = `${this.baseUrl}/${cleanEndpoint}`;

    const userType = this.b2bB2cService.getCurrentUserType();
    const sellingMode = userType === 'B2B' ? 'b2b' : 'b2c';
    const currentLanguage = this.translationService.getCurrentLanguage();

    const queryObject: Record<string, string> = { ...(params || {}) };
    if (options?.includeSellingMode !== false) {
      queryObject['sellingMode'] = sellingMode;
    }
    if (options?.includeLang !== false) {
      queryObject['lang'] = currentLanguage;
    }
    if (options?.noCache) {
      queryObject['noCache'] = 'true';
    }

    // Merge default query params
    const queryParams = new URLSearchParams(queryObject);

    // Don't add sellingMode/lang to courier-platform or delivery-profile requests
    const isCourierRequest =
      cleanEndpoint.startsWith('courier-platform/') || cleanEndpoint.startsWith('courier/');
    const isDeliveryRequest =
      cleanEndpoint.startsWith('delivery-profile/') || options?.useDeliveryToken === true;
    if (!isCourierRequest && !isDeliveryRequest) {
      // ✅ Add `?` or `&` depending on existing query string
      url += (url.includes('?') ? '&' : '?') + queryParams.toString();
    } else if (params && Object.keys(params).length > 0) {
      // For courier requests, only add provided params
      const courierParams = new URLSearchParams(params);
      url += (url.includes('?') ? '&' : '?') + courierParams.toString();
    }

    const token = this.resolveAuthToken(cleanEndpoint, options);
    const headers: any = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.get<T>(url, {
      headers,
      withCredentials: true, // Send cookies with request
      // Avoid SSR→browser transfer-cache replaying a stale/empty body (stuck loaders).
      ...(options?.noCache ? { transferCache: false as const } : {}),
    });
  }

  /** GET with response headers (e.g. X-Total-Count for shop pagination). */
  getWithMeta<T>(
    endpoint: string,
    params?: any,
    options?: { includeSellingMode?: boolean; includeLang?: boolean; noCache?: boolean; useDeliveryToken?: boolean },
  ): Observable<HttpResponseMeta<T>> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    let url = `${this.baseUrl}/${cleanEndpoint}`;

    const userType = this.b2bB2cService.getCurrentUserType();
    const sellingMode = userType === 'B2B' ? 'b2b' : 'b2c';
    const currentLanguage = this.translationService.getCurrentLanguage();

    const queryObject: Record<string, string> = { ...(params || {}) };
    if (options?.includeSellingMode !== false) {
      queryObject['sellingMode'] = sellingMode;
    }
    if (options?.includeLang !== false) {
      queryObject['lang'] = currentLanguage;
    }
    if (options?.noCache) {
      queryObject['noCache'] = 'true';
    }

    const queryParams = new URLSearchParams(queryObject);
    const isCourierRequest =
      cleanEndpoint.startsWith('courier-platform/') || cleanEndpoint.startsWith('courier/');
    const isDeliveryRequest =
      cleanEndpoint.startsWith('delivery-profile/') || options?.useDeliveryToken === true;
    if (!isCourierRequest && !isDeliveryRequest) {
      url += (url.includes('?') ? '&' : '?') + queryParams.toString();
    } else if (params && Object.keys(params).length > 0) {
      const courierParams = new URLSearchParams(params);
      url += (url.includes('?') ? '&' : '?') + courierParams.toString();
    }

    const token = this.resolveAuthToken(cleanEndpoint, options);
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http
      .get<T>(url, {
        headers,
        withCredentials: true,
        observe: 'response',
      })
      .pipe(
        map((res: HttpResponse<T>) => ({
          data: (res.body ?? null) as T,
          totalCount: this.parseTotalCountHeader(res.headers.get('X-Total-Count')),
        })),
      );
  }

  private parseTotalCountHeader(raw: string | null): number | null {
    if (raw == null || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  /** GET returning a Blob (e.g. PDF). Same URL/query/auth rules as `get`. */
  getBlob(endpoint: string, params?: Record<string, string>): Observable<Blob> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    let url = `${this.baseUrl}/${cleanEndpoint}`;

    const userType = this.b2bB2cService.getCurrentUserType();
    const sellingMode = userType === 'B2B' ? 'b2b' : 'b2c';
    const currentLanguage = this.translationService.getCurrentLanguage();
    const queryParams = new URLSearchParams({
      ...(params || {}),
      sellingMode,
      lang: currentLanguage,
    });
    const isCourierRequest =
      cleanEndpoint.startsWith('courier-platform/') || cleanEndpoint.startsWith('courier/');
    const isDeliveryRequest = cleanEndpoint.startsWith('delivery-profile/');
    if (!isCourierRequest && !isDeliveryRequest) {
      url += (url.includes('?') ? '&' : '?') + queryParams.toString();
    } else if (params && Object.keys(params).length > 0) {
      url += (url.includes('?') ? '&' : '?') + new URLSearchParams(params).toString();
    }

    const token = this.resolveAuthToken(cleanEndpoint);
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.get(url, {
      headers,
      withCredentials: true,
      responseType: 'blob',
    });
  }

  // POST method — third arg may be a legacy numeric timeout or `HttpPostOptions`.
  post<T>(endpoint: string, body: any, third?: number | HttpPostOptions): Observable<T> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;

    let customTimeout: number | undefined;
    let postOptions: HttpPostOptions | undefined;
    if (typeof third === 'number') {
      customTimeout = third;
    } else if (third && typeof third === 'object') {
      customTimeout = third.timeout;
      postOptions = third;
    }

    let token = this.resolveAuthToken(cleanEndpoint, postOptions);

    // If no token in localStorage, try customer session — never on delivery auth login/register
    if (!token && this.isBrowser && !this.isPublicDeliveryAuthEndpoint(cleanEndpoint)) {
      const userStr = this.browserService.getLocalStorage('loggedInUser');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          token = user.token || null;
        } catch (e) {
          // Ignore parse errors
        }
      }
    }

    // Don't add sellingMode/lang to FormData (for file uploads) or courier-platform requests
    const isFormData = body instanceof FormData;
    const isCourierRequest =
      cleanEndpoint.startsWith('courier-platform/') || cleanEndpoint.startsWith('courier/');
    const isDeliveryRequest =
      cleanEndpoint.startsWith('delivery-profile/') || postOptions?.useDeliveryToken === true;
    const isPublicDeliveryAuth = this.isPublicDeliveryAuthEndpoint(cleanEndpoint);

    const headers: any = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // For FormData, don't set Content-Type - let browser set it automatically with boundary
    // For JSON requests, set Content-Type
    if (!isFormData) {
      if (body && typeof body === 'object' && !(body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      if (!endpoint.includes('brand-request') && !isCourierRequest && !isDeliveryRequest) {
        const userType = this.b2bB2cService.getCurrentUserType();
        const sellingMode = userType === 'B2B' ? 'b2b' : 'b2c';
        const currentLanguage = this.translationService.getCurrentLanguage();
        const addSellingMode = postOptions?.includeSellingMode !== false;
        const addLang = postOptions?.includeLang !== false;

        if (body && typeof body === 'object' && !(body instanceof FormData)) {
          const b = body as Record<string, unknown>;
          if (!addSellingMode) {
            delete b['sellingMode'];
          } else {
            b['sellingMode'] = sellingMode;
          }
          if (!addLang) {
            delete b['lang'];
          } else {
            b['lang'] = currentLanguage;
          }
        }
      }
    }

    // Debug logging (only in development)
    if (!environment.production) {
      }

    // Use custom timeout if provided, otherwise use default based on endpoint
    // Email-related endpoints need longer timeout due to email sending delays
    const isEmailEndpoint = cleanEndpoint.includes('resend') || 
                           cleanEndpoint.includes('otp') || 
                           cleanEndpoint.includes('forgot-password') ||
                           cleanEndpoint.includes('reset-password') ||
                           cleanEndpoint.includes('signup') || // Signup sends OTP email
                           cleanEndpoint.includes('seller/login') || // Seller login may send 2FA email
                           cleanEndpoint.includes('login') || // Login endpoints may send emails
                           cleanEndpoint.includes('ai-automation'); // inventory rules can scan large catalogs
    const requestTimeout =
      customTimeout ?? (isEmailEndpoint ? 90000 : 30000); // 90s for email/automation, 30s default
    
    return this.http.post<T>(`${this.baseUrl}/${cleanEndpoint}`, body, {
      headers,
      withCredentials: !isPublicDeliveryAuth,
      transferCache: isPublicDeliveryAuth ? false : undefined,
    }).pipe(
      timeout(requestTimeout),
      catchError((error) => {
        // Enhance error messages for network errors
        if (error.name === 'TimeoutError') {
          return throwError(() => ({
            status: 0,
            message: 'Request timeout. Please check your internet connection and try again.',
            error: { message: 'Request timeout. The server took too long to respond.' }
          }));
        }
        return throwError(() => error);
      })
    );
  }

  /** POST with response headers (e.g. X-Total-Count from products/filter). */
  postWithMeta<T>(
    endpoint: string,
    body: any,
    third?: number | HttpPostOptions,
  ): Observable<HttpResponseMeta<T>> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;

    let customTimeout: number | undefined;
    let postOptions: HttpPostOptions | undefined;
    if (typeof third === 'number') {
      customTimeout = third;
    } else if (third && typeof third === 'object') {
      customTimeout = third.timeout;
      postOptions = third;
    }

    let token = this.resolveAuthToken(cleanEndpoint, postOptions);

    const isFormData = body instanceof FormData;
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const isCourierRequest =
      cleanEndpoint.startsWith('courier-platform/') || cleanEndpoint.startsWith('courier/');
    const isDeliveryRequest =
      cleanEndpoint.startsWith('delivery-profile/') || postOptions?.useDeliveryToken === true;
    if (!isCourierRequest && !isDeliveryRequest) {
      const userType = this.b2bB2cService.getCurrentUserType();
      const sellingMode = userType === 'B2B' ? 'b2b' : 'b2c';
      const currentLanguage = this.translationService.getCurrentLanguage();
      const addSellingMode = postOptions?.includeSellingMode !== false;
      const addLang = postOptions?.includeLang !== false;

      if (body && typeof body === 'object' && !(body instanceof FormData)) {
        const b = body as Record<string, unknown>;
        if (!addSellingMode) {
          delete b['sellingMode'];
        } else {
          b['sellingMode'] = sellingMode;
        }
        if (!addLang) {
          delete b['lang'];
        } else {
          b['lang'] = currentLanguage;
        }
      }
    }

    const isEmailEndpoint =
      cleanEndpoint.includes('resend') ||
      cleanEndpoint.includes('otp') ||
      cleanEndpoint.includes('forgot-password') ||
      cleanEndpoint.includes('reset-password') ||
      cleanEndpoint.includes('signup') ||
      cleanEndpoint.includes('seller/login') ||
      cleanEndpoint.includes('login');
    const requestTimeout = customTimeout ?? (isEmailEndpoint ? 90000 : 30000);

    return this.http
      .post<T>(`${this.baseUrl}/${cleanEndpoint}`, body, {
        headers,
        withCredentials: true,
        observe: 'response',
      })
      .pipe(
        timeout(requestTimeout),
        map((res: HttpResponse<T>) => ({
          data: (res.body ?? null) as T,
          totalCount: this.parseTotalCountHeader(res.headers.get('X-Total-Count')),
        })),
        catchError((error) => {
          if (error.name === 'TimeoutError') {
            return throwError(() => ({
              status: 0,
              message: 'Request timeout. Please check your internet connection and try again.',
              error: { message: 'Request timeout. The server took too long to respond.' },
            }));
          }
          return throwError(() => error);
        }),
      );
  }

  // PUT method
  put<T>(endpoint: string, body: any, options?: HttpPostOptions): Observable<T> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const token = this.resolveAuthToken(cleanEndpoint, options);
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.put<T>(`${this.baseUrl}/${cleanEndpoint}`, body, {
      headers,
      withCredentials: true,
    });
  }

  // PATCH method
  patch<T>(endpoint: string, body: any, options?: HttpPostOptions): Observable<T> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const token = this.resolveAuthToken(cleanEndpoint, options);
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.patch<T>(`${this.baseUrl}/${cleanEndpoint}`, body, {
      headers,
      withCredentials: true,
    });
  }

  // DELETE method
  delete<T>(endpoint: string, options?: HttpPostOptions): Observable<T> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const token = this.resolveAuthToken(cleanEndpoint, options);
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.delete<T>(`${this.baseUrl}/${cleanEndpoint}`, {
      headers,
      withCredentials: true,
    });
  }

  // Seller methods
  getSellerById(id: string) {
    const token = this.isBrowser ? this.browserService.getLocalStorage('token') : null;
    const headers: any = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // baseUrl already includes /api, so use baseUrl/seller/${id}
    return this.http.get<any>(`${this.baseUrl}/seller/${id}`, {
      headers,
      withCredentials: true,
    });
  }

  updateSeller(sellerData: any) {
    return this.http.put(`${this.baseUrl}/seller/${sellerData._id}`, sellerData);
  }

  // Unsplash API
  private UNSPLASH_API = 'https://api.unsplash.com/search/photos';
  private ACCESS_KEY = 'YOUR_UNSPLASH_ACCESS_KEY';

  getImageForSubCategory(query: string) {
    return this.http.get(
      `${this.UNSPLASH_API}?query=${encodeURIComponent(query)}&client_id=${this.ACCESS_KEY}&per_page=1`,
    );
  }

  // PDF Download
  downloadPDF(route: any) {
    return this.http.get(`${this.baseUrl}/${route}`, {
      responseType: 'blob',
    });
  }

  // Orders
  getOrders(seller: any, route: any, filters: any = {}): Observable<any> {
    let params = new HttpParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        params = params.set(key, filters[key]);
      }
    });
    params = params.set('sellerId', seller);

    return this.http.get(`${this.baseUrl}/${route}`, { params });
  }
}
